import { useMemo, useState, useEffect } from "react";
import { splitOnCapitals } from "../../Helpers/Functions";
import logo from '../../images/unite_logo_no_bg.png';
import logoDark from '../../images/unite_logo_no_bg_dark.png';

interface ProgressLoaderProps {
  loaders?: { [key: string]: boolean };
}

export default function ProgressLoader({ loaders = {} }: ProgressLoaderProps) {
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [everStartedLoading, setEverStartedLoading] = useState(new Set<string>());

  const loadingNicknames = {
    isInspectionsLoading: 'INSPECTION CHECKLISTS',
    isInspectionTaskLoading: 'INSPECTION TASKS',
    isProjectsLoading: 'PROJECTS',
    isFilesLoading: 'MAPS / DRAWINGS',
    isSequencesLoading: 'SEQUENCES',
    isLogsTypesLoading: 'CHANGELOG TYPES',
    isDisciplineLoading: 'DISCIPLINES',
    isUsersLoading: 'ALL USERS',
    isCommentsLoading: 'COMMENTS',
    isLogsLoading: 'CHANGELOGS',
    isIssuesLoading: 'ISSUES',
    isStatusLoading: 'STATUSES',
    isCategoriesLoading: 'CATEGORIES',
    isPunchlistLoading: 'PUNCH LISTS',
    isPunchlistImagesLoading: 'PUNCH LIST IMAGES',
    isAdHocUnitsLoading: 'AD HOC UNITS',
    isModelsLoading: 'MODELS',
    isRelatedChecklistDetailsLoading: 'CHECKLIST DETAILS',
    isCreatingPunchListLoading: 'PUNCH LIST',
    isRootCausesLoading: 'ROOT CAUSES',
    isTechnologyLoading: 'TECHNOLOGY',
    isRegionsLoading: 'REGIONS',
    isLinkDisciplineIssueLoading: 'DISCIPLINE ISSUES',
    isLinkDisciplineTechCategoryLoading: 'DISCIPLINE CATEGORIES',
    isInspectionTaskLinksLoading: 'CHECKLIST LINKS',
    isOnHoldCausesLoading: 'ON HOLD CAUSES',
    isBusinessUnitsLoading: 'BUSINESS UNITS',
    isDrilldownMappingsLoading: 'DRILLDOWN MAPPINGS',
    isTasksLoading: 'TASKS',
  };

  useEffect(() => {
    const currentlyLoading = Object.keys(loaders).filter(key => loaders[key] === true);
    if (currentlyLoading.length > 0) {
      setEverStartedLoading(prev => {
        const newSet = new Set(prev);
        currentlyLoading.forEach(item => newSet.add(item));
        return newSet;
      });
    }
  }, [loaders]);

  const { completedItems, currentlyLoading, totalItems, progress } = useMemo(() => {
    const allItems = Object.keys(loaders);
    const loadingItems = allItems.filter(key => loaders[key] === true);
    const completedCount = Array.from(everStartedLoading).filter(key => !loaders[key]).length;
    
    let progressPercent = 0;
    
    if (everStartedLoading.size === 0) {
      progressPercent = 0;
    } else if (loadingItems.length === 0 && everStartedLoading.size > 0) {
      progressPercent = 100;
    } else {
      progressPercent = Math.round((completedCount / allItems.length) * 100);
      if (loadingItems.length > 0) {
        progressPercent = Math.min(95, progressPercent);
      }
    }
    
    const currentItem = loadingItems.length > 0 ? loadingItems[0] : null;
    
    return {
      completedItems: completedCount,
      currentlyLoading: currentItem,
      totalItems: allItems.length,
      progress: progressPercent
    };
  }, [loaders, everStartedLoading]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSmoothProgress(progress);
    }, 100);
    return () => clearTimeout(timeout);
  }, [progress]);

  const getCurrentLoadingName = (key: string | null) => {
    if (!key) return '';
    return loadingNicknames[key] || splitOnCapitals(key.replace('is', '').replace('Loading', '') ?? '')?.toUpperCase();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-screen h-screen bg-white dark:bg-gray-1000">
      <div className="flex flex-col items-center justify-center max-w-md w-full px-8 -mt-12">
        {/* Logo */}
        <div className="mb-8">
          <img 
            src={logoDark} 
            alt="UNITE Logo" 
            className="max-w-xs md:max-w-sm lg:max-w-md w-full h-auto object-contain hidden dark:block"
          />
          <img 
            src={logo} 
            alt="UNITE Logo" 
            className="max-w-xs md:max-w-sm lg:max-w-md w-full h-auto object-contain dark:hidden"
          />
        </div>
        
        {/* Progress Section */}
        <div className="w-full mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Downloading Application Data...
            </span>
            <span className="text-sm font-bold text-[#fdb419]">
              {smoothProgress}%
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-4 dark:bg-gray-700 shadow-inner overflow-hidden">
            <div 
              className="bg-gradient-to-r from-[#fdb419] to-[#f59e0b] h-4 rounded-full transition-all duration-500 ease-out shadow-sm relative"
              style={{ width: `${smoothProgress}%` }}
            >
              <div className="h-full w-full rounded-full bg-white opacity-20"></div>
              <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
            </div>
          </div>
          
          {/* Progress Text */}
          <div className="flex justify-between items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{completedItems} of {totalItems} completed</span>
            {currentlyLoading && (
              <span className="font-medium">
                Loading {getCurrentLoadingName(currentlyLoading)}...
              </span>
            )}
          </div>
        </div>

        {/* Current Loading Items List */}
        {Object.keys(loaders).some(key => loaders[key]) && (
          <div className="w-full">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-40 overflow-y-auto">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Currently Loading:
              </h3>
              <div className="space-y-1">
                {Object.keys(loaders).map(key => {
                  if (!loaders[key]) return null;
                  
                  const loadingName = getCurrentLoadingName(key);
                  return (
                    <div key={key} className="flex items-center text-xs">
                      <div className="w-2 h-2 bg-[#fdb419] rounded-full mr-2 animate-pulse"></div>
                      <span className="text-gray-600 dark:text-gray-400">{loadingName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Loading Complete Message */}
        {progress === 100 && (
          <div className="mt-4 text-center">
            <div className="text-green-600 dark:text-green-400 font-semibold text-sm flex items-center justify-center animate-bounce">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center mr-2">
                <span className="text-white text-xs">✓</span>
              </div>
              Download Complete!
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 animate-pulse">
              Initializing application...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}